// SPEC-19 · the one-question-per-screen engine, for every assessment after
// Dados gerais (the pattern of SPEC-12). An introduction explains, then one
// question per screen with "Anterior", "Continuar" (or "Terminar" on the
// last visible one) and "Sair".
//
// SPEC-27: it opens on its introduction, from Conversa as from the hub (the
// founder's answer 4), and « Sair » and ← on the introduction lead back to
// the tab it was opened from.
//
// SPEC-28: « Terminar » opens the recap. « Alterar » reopens a question and
// comes back to it; « Confirmar respostas » finishes the run and returns to
// the tab, where Conversa thanks her (the founder's answer 1). A finished
// assessment opens on the same recap, read-only: no redo from the app.
//
// Which questions show, the counter, where a reopened run resumes, which
// answer is valid and which answers a change erases are poweryourflow-lib's
// rules, read from its registry entry. Each answer goes to the data layer
// when she taps "Continuar"; the screen shows what the lib returns.
//
// SPEC-31: a questionnaire stays in PT whatever the app's language, from
// its introduction to its recap, the hub's words for it included: every
// word here is PT's `copy`, never the language's. In another language its
// screens are marked PT. The language reaches it through the context
// (useLanguage), for the cards that translate it later.

import { useEffect, useRef, useState } from "react";
import {
  ASSESSMENT_FACTS,
  answerError,
  applyAnswer,
  applyScreen,
  fieldsOf,
  progress,
  questionnaireFor,
  resumeAt,
  screenError,
  visibleQuestions,
} from "poweryourflow-lib";
import type { FieldValues, Questionnaire } from "poweryourflow-lib";

import { PyfBubble } from "../components/Chat";
import type { DataLayer, HubState } from "../data";
import { HTML_LANG } from "../i18n/language";
import { useLanguage } from "../i18n/LanguageProvider";
import { copy } from "../i18n/pt-PT";
import { originOf, originPath, rememberFinished } from "../lib/place";
import { paths, useRouter } from "../lib/router";
import type { AnswerValue, Answers, AssessmentId } from "../schema/types";
import { AnswerInput } from "./AnswerInput";
import { Recap } from "./Recap";
import { recapLines } from "./recap";
import { ScreenFields } from "./ScreenFields";

const text = copy.questionnaire;

/** The signed-in account and the three calls of the data layer this screen makes. */
export interface AssessmentSource {
  accountId(): Promise<string | null>;
  data(): Pick<DataLayer, "loadHub" | "recordAnswer" | "recordFinish">;
}

/**
 * loading → intro → question ⇄ question → recap ⇄ question (« Alterar ») → back to the tab.
 * A run with every question answered reopens on the recap; a finished one on the recap read-only.
 */
type Phase = "loading" | "intro" | "question" | "recap" | "readOnly";

/** The visible questions that are their own screen, in order; a complementary field rides on its parent's. */
const screensOf = (items: Questionnaire, answers: Answers) =>
  visibleQuestions(items, answers).filter((question) => question.screen !== false);

function codeOf(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) return String(error.code);
  if (error instanceof Error) return error.name;
  return typeof error;
}

const wideButton =
  "flex h-[var(--control-h)] items-center justify-center gap-2 rounded-pill px-6 text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";
const primary = `${wideButton} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`;
const secondary = `${wideButton} border border-line-20 bg-white text-dark hover:bg-cream-deep`;
const iconButton =
  "flex size-[var(--touch-min)] shrink-0 items-center justify-center rounded-pill text-title text-dark " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf hover:bg-cream-deep";
const exitLink = "flex h-[var(--touch-min)] items-center px-1 text-body font-semibold text-violet";

/** The flow, marked PT when the app speaks another language; one tree in every language, so a change keeps her place. */
export function AssessmentFlow({ assessmentId, source }: { assessmentId: AssessmentId; source: AssessmentSource }) {
  const { language } = useLanguage();
  return (
    <div lang={language === "pt" ? undefined : HTML_LANG.pt} className="contents">
      <Flow assessmentId={assessmentId} source={source} />
    </div>
  );
}

function Flow({ assessmentId, source }: { assessmentId: AssessmentId; source: AssessmentSource }) {
  const { navigate, leaveTo } = useRouter();
  const items = questionnaireFor(assessmentId);
  const words = copy.questionnaires[assessmentId];

  const [phase, setPhase] = useState<Phase>("loading");
  const [answers, setAnswers] = useState<Answers>({});
  const [current, setCurrent] = useState<string | null>(null);
  const [draft, setDraft] = useState<AnswerValue | undefined>(undefined);
  /** What she typed in the fields under an option, by field id; seeded from the stored answers. */
  const [fieldDraft, setFieldDraft] = useState<Answers>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // What the async steps read between renders. `amending`: the question was
  // opened by « Alterar », so its « Continuar » leads back to the recap.
  const session = useRef({ accountId: "", touched: false, amending: false });

  const show = (questionId: string, from: Answers) => {
    setCurrent(questionId);
    const question = items?.questions.find((candidate) => candidate.id === questionId);
    // A stepper starts at its item's value (Sono: 8 h), already an answer she
    // can keep; a written answer starts empty, which the lib accepts when it
    // is optional (LEAF-Q's « Comentários »: « Continuar » always on).
    setDraft(from[questionId] ?? question?.initial ?? (question?.kind === "text" ? "" : undefined));
    setFieldDraft(from);
    setFailed(false);
    setPhase("question");
  };

  /** The recap, unless a visible question has no answer: then that question, so the recap never shows an empty line. */
  const toRecap = (from: Answers) => {
    const missing = items ? resumeAt(items, from) : null;
    if (missing) {
      show(missing, from);
      return;
    }
    session.current.amending = false;
    setFailed(false);
    setPhase("recap");
  };

  useEffect(() => {
    let alive = true;

    const begin = (hub: HubState, first: boolean) => {
      const run = hub.assessments.find((entry) => entry.assessmentId === assessmentId)?.run ?? null;
      session.current = { ...session.current, accountId: hub.accountId };
      if (run?.state === "done") {
        // Confirmed: read-only, no control changes an answer (SPEC-28).
        setAnswers(run.answers);
        setPhase("readOnly");
        return;
      }
      const from = run?.state === "progress" ? run.answers : {};
      setAnswers(from);
      if (Object.keys(from).length > 0) {
        // Reopened mid-way: straight back to her question (SPEC-12, "Sair"), or to the recap when all are answered.
        toRecap(from);
      } else if (first) {
        setPhase("intro");
      }
    };

    const start = async () => {
      const accountId = await source.accountId();
      if (!alive) return;
      if (!accountId) {
        navigate(paths.entry, { replace: true });
        return;
      }
      const hub = await source.data().loadHub(accountId, (fresh) => {
        // Another device may have got further; that counts only until she answers here.
        if (alive && !session.current.touched) begin(fresh, false);
      });
      if (alive) begin(hub, true);
    };
    start().catch((error: unknown) => {
      console.error(`[${assessmentId}] could not open`, { code: codeOf(error) });
      if (alive) navigate(originPath(originOf(assessmentId)), { replace: true });
    });

    return () => {
      alive = false;
    };
    // Runs once per visit; the async steps read the latest state through refs.
  }, []);

  if (!items || !words) {
    console.error(`[${assessmentId}] no item list or no words for this assessment`);
    return null;
  }

  // Read when she leaves: the last opening counts (SPEC-27, Action list).
  const origin = originOf(assessmentId);
  const leave = () => leaveTo(originPath(origin));
  const backButton = (
    <button type="button" onClick={leave} className={iconButton}>
      <span aria-hidden="true">←</span>
      <span className="sr-only">{text.back}</span>
    </button>
  );

  /** « Começar »: the first question without an answer. */
  const startQuestions = () => {
    const first = resumeAt(items, answers);
    if (first) show(first, answers);
    else toRecap(answers);
  };

  /** « Confirmar respostas »: the run is finished, then back to the tab; Conversa thanks her (SPEC-28). */
  const confirm = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const done = await source.data().recordFinish(session.current.accountId, assessmentId);
      if (originPath(origin) === paths.conversa) {
        const closing = items.closing?.(done.answers);
        rememberFinished({ assessmentId, ...(closing !== undefined && { closing }) });
      }
      leave();
    } catch (error) {
      console.error(`[${assessmentId}] finish not stored`, { code: codeOf(error) });
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") {
    return <main aria-busy="true" aria-label={text.loading} className="min-h-dvh bg-cream" />;
  }

  if (phase === "recap" || phase === "readOnly") {
    const readOnly = phase === "readOnly";
    const lines = recapLines(items, answers, words);
    const row = copy.assessments.rows[assessmentId];
    // LEAF-Q counts in parts on the hub, in questions here: the recap lists questions. PT's units, as the whole recap.
    const unit = ASSESSMENT_FACTS[assessmentId].unit === "symptoms" ? copy.conversa.units.symptoms : copy.conversa.units.questions;
    const lastScreen = screensOf(items, answers).at(-1);
    return (
      <Recap
        kicker={text.recap.kicker(row.kicker, row.title)}
        lines={lines}
        count={text.recap.count(lines.length, unit)}
        readOnly={readOnly}
        busy={busy}
        failed={failed}
        // Before confirming, ← goes back to the last question; read-only, back to the tab.
        onBack={() => {
          session.current.touched = true;
          if (readOnly || !lastScreen) leave();
          else show(lastScreen.id, answers);
        }}
        onChange={(questionId) => {
          // A late refresh from the server must not pull her back to the recap.
          session.current.touched = true;
          session.current.amending = true;
          show(questionId, answers);
        }}
        onConfirm={() => void confirm()}
      />
    );
  }

  if (phase === "intro") {
    return (
      <main key={phase} className="screen-enter flex min-h-dvh flex-col bg-cream px-5 pt-4 pb-5">
        <header>
          {backButton}
        </header>
        <div className="flex flex-1 flex-col justify-center gap-4">
          <p className="text-label font-semibold tracking-[var(--ls-label)] text-violet uppercase">
            {copy.assessments.rows[assessmentId].kicker}
          </p>
          <h1 className="text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">{words.intro.title}</h1>
          <p className="text-body-lg leading-[var(--lh-body)] text-muted">{words.intro.text}</p>
        </div>
        {failed && <p role="alert" className="pb-3 text-body-sm text-orange-ink">{text.saveFailed}</p>}
        <button type="button" onClick={startQuestions} className={`${primary} w-full`}>
          {text.start}
        </button>
      </main>
    );
  }

  // One question per screen.
  const question = items.questions.find((candidate) => candidate.id === current);
  const said = question ? words.questions[question.id] : undefined;
  if (!question || !said) return null;
  const id = question.id;

  // A screen answered by its fields (SPEC-20) has no option to tap: « Continuar »
  // records the lib's `filled` answer with them, its second button `none`.
  const { byFields } = question;
  const value = byFields ? byFields.filled : draft;
  // The fields this choice shows under its options (SPEC-21), with what she typed in them.
  const chosen = value !== undefined && answerError(question, value) === undefined;
  const fields = chosen ? fieldsOf(items, applyAnswer(items, answers, id, value), id) : [];
  const typed = Object.fromEntries(fields.map((field) => [field.id, fieldDraft[field.id]]));
  const valid = chosen && screenError(items, answers, id, value, typed) === undefined;
  // The counter and the button read the answers as they would be with this
  // choice: Q1 "nunca" turns "1 / 4" into "1 / 1" and "Continuar" into "Terminar".
  const preview = valid ? applyScreen(items, answers, id, value, typed) : answers;
  const screens = screensOf(items, preview);
  const position = screens.findIndex((screen) => screen.id === id) + 1;
  const { total } = progress(items, preview);
  // The counter's total, not the screens shown now: before she answers a
  // deciding question its branch is hidden, yet still counted (BEDA-Q Q8
  // reads "8 / 9" and "Continuar" until "Não" makes it "8 / 8", "Terminar").
  const { amending } = session.current;
  const last = position === total && !amending;

  // Once she moves or chooses here, a late refresh from the server must not
  // pull her back to the question it would resume at.
  const touch = () => {
    session.current.touched = true;
  };

  const fieldText = (fieldId: string) => words.questions[fieldId];
  const typeField = (fieldId: string, typedValue: AnswerValue) => {
    touch();
    setFieldDraft((typedSoFar) => ({ ...typedSoFar, [fieldId]: typedValue }));
  };

  const previous = () => {
    touch();
    // Opened from the recap: back to it, the answer unchanged.
    if (amending) {
      toRecap(answers);
      return;
    }
    const before = screens[position - 2];
    if (before) show(before.id, answers);
    // With no « Anterior » on the screen, ← on the first one is Sair (SPEC-20).
    else if (byFields) leave();
    else setPhase("intro");
  };

  /** Stores this screen's answer, then shows the next screen, or the recap after the last or after « Alterar ». */
  const record = async (answer: AnswerValue, withFields: FieldValues) => {
    const { accountId } = session.current;
    setBusy(true);
    touch();
    try {
      const data = source.data();
      const run = await data.recordAnswer(accountId, assessmentId, id, answer, withFields);
      setAnswers(run.answers);
      const after = screensOf(items, run.answers);
      const following = after[after.findIndex((screen) => screen.id === id) + 1];
      if (following && !session.current.amending) show(following.id, run.answers);
      else toRecap(run.answers);
    } catch (error) {
      // The question id and the code only: the answer is health data.
      console.error(`[${assessmentId}] answer not stored`, { question: id, code: codeOf(error) });
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (valid && !busy && value !== undefined) void record(value, typed);
  };
  // « Não sinto este sintoma »: absent, and on to the next at once, with no confirmation.
  const none = () => {
    if (byFields && !busy) void record(byFields.none, {});
  };

  return (
    <main className="flex min-h-dvh flex-col bg-cream px-5 pt-4 pb-5">
      <header className="flex items-center gap-3">
        <button type="button" onClick={previous} className={iconButton}>
          <span aria-hidden="true">←</span>
          <span className="sr-only">{text.previousScreen}</span>
        </button>
        <div
          role="progressbar"
          aria-label={text.progress}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={position}
          className="h-1 flex-1 overflow-hidden rounded-pill bg-line-10"
        >
          {/* A transform, as in Dados gerais: it slides without a layout pass and holds still under reduced motion. */}
          <div
            className="h-full w-full rounded-pill bg-violet transition-transform duration-[var(--dur-step)] ease-pyf motion-reduce:transition-none"
            style={{ transform: `translateX(${(position / total - 1) * 100}%)` }}
          />
        </div>
        <span data-testid="counter" className="text-body-sm font-medium text-muted">
          {text.counter(position, total)}
        </span>
        <button type="button" onClick={leave} className={exitLink}>
          {text.exit}
        </button>
      </header>

      {/* Keyed by question, so each new one arrives with the step's fade and rise. */}
      <section key={id} className="step-enter flex flex-1 flex-col gap-5 pt-5">
        {/* The part of a long questionnaire this screen belongs to (LEAF-Q, « 1 · LESÕES »). */}
        {said.section && (
          <p data-testid="section" className="text-label font-semibold tracking-[var(--ls-label)] text-violet uppercase">
            {said.section}
          </p>
        )}
        {said.help && (
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-1 size-8 shrink-0 rounded-pill bg-violet" />
            <PyfBubble enter={false}>{said.help}</PyfBubble>
          </div>
        )}
        <h1 className="text-question leading-[var(--lh-title)] font-semibold tracking-[var(--ls-heading)]">{said.title}</h1>
        {byFields ? (
          <ScreenFields fields={fields} text={fieldText} values={fieldDraft} onChange={typeField} />
        ) : (
          <AnswerInput
            question={question}
            text={said}
            value={draft}
            onChange={(choice) => {
              touch();
              setDraft(choice);
            }}
            fields={{ questions: fields, text: fieldText, values: fieldDraft, onChange: typeField }}
          />
        )}
      </section>

      {failed && <p role="alert" className="pt-4 text-body-sm text-orange-ink">{text.saveFailed}</p>}
      {byFields ? (
        <footer className="flex flex-col gap-3 pt-6">
          <button type="button" disabled={busy} onClick={none} className={secondary}>
            {said.none}
          </button>
          <button type="button" disabled={!valid || busy} onClick={next} className={primary}>
            {last ? text.finish : text.next}
          </button>
        </footer>
      ) : (
        <footer className="flex gap-3 pt-6">
          <button type="button" onClick={previous} className={secondary}>
            {text.previous}
          </button>
          <button type="button" disabled={!valid || busy} onClick={next} className={`${primary} flex-1`}>
            {last ? text.finish : text.next}
          </button>
        </footer>
      )}
    </main>
  );
}
